<div
  class="fi-trigger"
  tabindex="0"
  role="button"
  matTooltip="Open the Device System Activity Log guide"
  aria-label="How the Device System Activity Log Works"
  (click)="open()"
  (keydown.enter)="open()"
>
  <mat-icon class="fi-trigger-icon">menu_book</mat-icon>
</div>

<div
  class="custom-container system-activity-log-info-root info-panel-root"
  [class.active]="isOpen"
>
  <div class="custom-modal custom-modal-l">
    <div class="custom-modal-dialog">
      <div class="custom-modal-content">
        <div class="custom-modal-header">
          <h4 class="custom-modal-title">How the Device System Activity Log Works</h4>
          <div class="fi-header-actions">
            <button
              type="button"
              class="fi-export-btn"
              (click)="exportPdf()"
              aria-label="Export as PDF"
            >
              <mat-icon>picture_as_pdf</mat-icon>
              Export as PDF
            </button>
            <button type="button" class="custom-close-btn" (click)="close()">
              &times;
            </button>
          </div>
        </div>

        <div class="custom-modal-body fi-body">
          <p class="fi-intro">
            This log is an automatic record of anomalies the server notices
            in a device's packets, and any corrective command it sends back
            in response. It is <strong>not</strong> a record of every packet
            a device sends &mdash; only the entries where something actually
            needed attention.
          </p>

          <section class="fi-section">
            <h5 class="fi-h5">1. What this log is, and isn't</h5>
            <p class="fi-p">
              You'll find this log in two places, both reading the exact
              same data:
            </p>
            <ul class="fi-rule-list">
              <li><strong>On a device's own Device Details page</strong> &mdash; automatically filtered to just that device.</li>
              <li><strong>On its own standalone page</strong> &mdash; covering every device, with its own IMEI search and an Activity Type filter.</li>
            </ul>
            <div class="fi-callout">
              <strong>Not the same as the Login Packet / Health Packet history.</strong>
              Those two panels record <em>every single packet</em> a device
              sends, good or bad &mdash; a complete raw history. This log
              only ever gets a new row when the server actually spotted
              something worth flagging. A device that's behaving perfectly
              will never appear here at all.
            </div>
          </section>

          <section class="fi-section">
            <h5 class="fi-h5">2. How an entry gets created</h5>
            <p class="fi-p">
              Every time a device checks in (a login or health packet), the
              server compares what it reported against what's expected. Most
              packets pass with nothing to say. When something doesn't
              match, the server decides whether it's something it can fix by
              itself.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 600 390"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="A device sends a packet. If nothing looks wrong, no entry is created. If something looks wrong and the server has no fix to send back, it logs the anomaly only. If it does have a fix, it sends the correction to the device and then logs that."
              >
                <defs>
                  <marker id="salArrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
                  </marker>
                  <marker id="salArrowMuted" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--theme-text-muted)" />
                  </marker>
                  <marker id="salArrowDanger" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-danger)" />
                  </marker>
                </defs>

                <rect x="330" y="20" width="220" height="46" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="440" y="40" text-anchor="middle" font-size="9.4" font-weight="600" fill="var(--theme-text)">Device sends a packet</text>
                <text x="440" y="56" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">login or health</text>

                <line x1="440" y1="66" x2="440" y2="90" stroke="currentColor" stroke-width="1.6" marker-end="url(#salArrow)" />

                <polygon points="440,90 530,136 440,182 350,136" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="440" y="132" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Does anything in it</text>
                <text x="440" y="146" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">look wrong?</text>

                <line x1="350" y1="136" x2="212" y2="136" stroke="var(--theme-text-muted)" stroke-width="1.4" marker-end="url(#salArrowMuted)" />
                <text x="280" y="126" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">no</text>
                <rect x="20" y="111" width="190" height="50" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="115" y="132" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Nothing logged</text>
                <text x="115" y="147" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">most packets look fine</text>

                <line x1="440" y1="182" x2="440" y2="206" stroke="var(--info-danger)" stroke-width="1.6" marker-end="url(#salArrowDanger)" />
                <text x="456" y="198" font-size="8.2" fill="var(--info-danger)">yes</text>

                <polygon points="440,206 530,252 440,298 350,252" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="440" y="248" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Can the server push</text>
                <text x="440" y="262" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">a fix back to it?</text>

                <line x1="350" y1="252" x2="212" y2="252" stroke="var(--theme-text-muted)" stroke-width="1.4" marker-end="url(#salArrowMuted)" />
                <text x="280" y="242" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">no</text>
                <rect x="20" y="227" width="190" height="50" rx="10" fill="var(--info-accent-fill)" stroke="var(--info-accent)" />
                <text x="115" y="248" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--info-accent-strong)">Logged only</text>
                <text x="115" y="263" text-anchor="middle" font-size="8.2" fill="var(--info-accent-strong)">e.g. clock drift, low voltage</text>

                <line x1="440" y1="298" x2="440" y2="322" stroke="var(--info-danger)" stroke-width="1.6" marker-end="url(#salArrowDanger)" />
                <text x="456" y="314" font-size="8.2" fill="var(--info-danger)">yes</text>

                <rect x="330" y="324" width="220" height="50" rx="10" fill="var(--info-danger-fill)" stroke="var(--info-danger)" stroke-width="1.6" />
                <text x="440" y="345" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--info-danger)">Correction sent to device,</text>
                <text x="440" y="360" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--info-danger)">then logged</text>
              </svg>
              <figcaption class="fi-caption">
                A device that's behaving as expected produces no entry at
                all. Of the anomalies the server does catch, some are just
                worth recording (it has no way to fix them from its end);
                others come with a specific correct value the server already
                knows, which it sends to the device before logging what
                happened.
              </figcaption>
            </figure>

            <p class="fi-p"><strong>Logged only</strong> &mdash; no command sent to the device:</p>
            <ul class="fi-rule-list">
              <li><strong>Clock drift</strong> (<code>FUT_RTC</code> / <code>PST_RTC</code>) &mdash; the device's own clock is more than 5 minutes ahead of or behind the server's.</li>
              <li><strong>Low power</strong> (<code>MAIN_VTG</code> / <code>INT_BAT_VTG</code>) &mdash; the vehicle's main power or the device's internal backup battery has dropped below a safe level.</li>
              <li><strong>Invalid GPS fix</strong> (<code>LAT_LONG</code>) &mdash; the device reported (0, 0) as its position, meaning it hasn't got a real GPS lock yet.</li>
              <li><strong>Unregistered device</strong> (<code>DNF</code>) &mdash; in some cases, when the IMEI/ID isn't recognized at all and there's nothing sensible to redirect it to.</li>
            </ul>
            <p class="fi-p"><strong>Logged, and a correction sent to the device:</strong></p>
            <ul class="fi-rule-list">
              <li><strong>Panic re-acknowledgement</strong> (<code>SACK</code>) &mdash; the device raised a panic/emergency alert without a valid server address to properly report it to, so the server resends its own address.</li>
              <li><strong>Server address correction</strong> (<code>PrimaryIp</code> / <code>SecondaryIp</code>) &mdash; the device's stored server address doesn't match what's configured for it (see Section 3).</li>
              <li><strong>Identity correction</strong> (<code>VIN</code> / <code>UIN</code>) &mdash; the chassis number or unique ID the device is reporting doesn't match what's on file (see Section 3).</li>
              <li><strong>Wire-cut reset</strong> (<code>WIRE_CUT_CRST</code>) &mdash; the device reports its wiring has been cut or tampered with, and the server sends it a reset command &mdash; at most once every 5 days per device, so a device stuck reporting this doesn't get reset over and over.</li>
              <li><strong>Unregistered device</strong> (<code>DNF</code>) &mdash; in other cases, the server redirects the device to a fallback address before logging it.</li>
            </ul>
          </section>

          <section class="fi-section">
            <h5 class="fi-h5">3. Its connection to Device State Config, Device VIN Config and AIS-140 tickets</h5>
            <p class="fi-p">
              A device's assigned state and chassis number (VIN) can be set in
              three ways:
            </p>
            <ul class="fi-rule-list">
              <li><strong>Device State Config</strong>, where an admin sets the device's state.</li>
              <li><strong>Device VIN Config</strong>, where an admin sets the device's VIN.</li>
              <li><strong>An AIS-140 ticket being created</strong> for the vehicle, including a change-request or renewal ticket. This always sets the device's VIN to the ticket's VIN. It sets the device's state to the ticket's RTO state <strong>only if the device has no state yet</strong>; a device that already has one keeps it. The automatic firmware-update (FOTA) batch the ticket raises, if one is needed, uses the same state.</li>
            </ul>
            <p class="fi-p">
              None of these write anything to this log by themselves. They
              only update the device's own record quietly in the background.
              An entry only appears here later, when the device actually
              checks in again and its packet still disagrees with what was
              just set.
            </p>

            <figure class="fi-figure">
              <svg
                viewBox="0 0 460 360"
                xmlns="http://www.w3.org/2000/svg"
                role="img"
                aria-label="An admin updates Device State Config or Device VIN Config, or an AIS-140 ticket is created, which silently updates the device's record with no log entry. The next time the device checks in, if its packet already matches, nothing is logged. If it still disagrees, the server sends the correct value back and logs that."
              >
                <defs>
                  <marker id="salArrow2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="currentColor" />
                  </marker>
                  <marker id="salArrowMuted2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--theme-text-muted)" />
                  </marker>
                  <marker id="salArrowDanger2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L10,5 L0,10 z" fill="var(--info-danger)" />
                  </marker>
                </defs>

                <rect x="170" y="20" width="260" height="50" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="300" y="40" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">State / VIN Config updated,</text>
                <text x="300" y="55" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">or an AIS-140 ticket is created</text>

                <line x1="300" y1="70" x2="300" y2="92" stroke="currentColor" stroke-width="1.6" marker-end="url(#salArrow2)" />

                <rect x="170" y="94" width="260" height="46" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="300" y="114" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Stored on the device record</text>
                <text x="300" y="128" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">no log entry yet</text>

                <line x1="300" y1="140" x2="300" y2="162" stroke="currentColor" stroke-width="1.6" marker-end="url(#salArrow2)" />

                <polygon points="300,164 390,210 300,256 210,210" fill="var(--theme-surface)" stroke="var(--theme-border)" />
                <text x="300" y="206" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">Does the device's next</text>
                <text x="300" y="220" text-anchor="middle" font-size="8.6" fill="var(--theme-text)">packet already match?</text>

                <line x1="210" y1="210" x2="192" y2="210" stroke="var(--theme-text-muted)" stroke-width="1.4" marker-end="url(#salArrowMuted2)" />
                <text x="150" y="200" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">yes</text>
                <rect x="20" y="185" width="170" height="50" rx="10" fill="var(--theme-surface-alt)" stroke="var(--theme-border)" />
                <text x="105" y="206" text-anchor="middle" font-size="8.6" font-weight="600" fill="var(--theme-text)">Nothing logged</text>
                <text x="105" y="221" text-anchor="middle" font-size="8.2" fill="var(--theme-text-muted)">device already agreed</text>

                <line x1="300" y1="256" x2="300" y2="280" stroke="var(--info-danger)" stroke-width="1.6" marker-end="url(#salArrowDanger2)" />
                <text x="316" y="272" font-size="8.2" fill="var(--info-danger)">no</text>

                <rect x="170" y="282" width="260" height="56" rx="10" fill="var(--info-danger-fill)" stroke="var(--info-danger)" stroke-width="1.6" />
                <text x="300" y="303" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--info-danger)">Server sends the correct</text>
                <text x="300" y="318" text-anchor="middle" font-size="8.6" font-weight="700" fill="var(--info-danger)">value back, then logs it</text>
              </svg>
              <figcaption class="fi-caption">
                If the device's very next check-in already reports the value
                that was just configured, nothing is ever logged for that
                change &mdash; this log only captures disagreements, never
                the quiet successful case. If it still disagrees (the device
                hasn't picked up the change yet, or the config was corrected
                because the device was wrong), the server pushes the correct
                value and logs the reconciliation as a
                <code>PrimaryIp</code> / <code>SecondaryIp</code> entry (from
                Device State Config) or a <code>VIN</code> / <code>UIN</code>
                entry (from Device VIN Config or a new ticket).
              </figcaption>
            </figure>

            <div class="fi-callout">
              <strong>Seeing a <code>VIN</code> entry right after a ticket was
              raised?</strong> That's normal. The ticket linked the device to
              the vehicle's VIN, and the device's next check-in still
              reported its old (or blank) VIN, so the server pushed the
              correct one. A <code>PrimaryIp</code> / <code>SecondaryIp</code>
              entry is about the device's <strong>own</strong> state. If the
              device already had a state, that state is still the one used,
              even when the ticket's RTO state is different. To move the
              device to another state, use Device State Config.
            </div>
          </section>

          <section class="fi-section">
            <h5 class="fi-h5">4. How long entries stick around</h5>
            <ul class="fi-rule-list">
              <li>Every entry is deleted automatically once it's <strong>15 days</strong> old &mdash; <em>except</em> <code>SACK</code>, <code>PrimaryIp</code>, <code>SecondaryIp</code>, <code>VIN</code>, <code>UIN</code>, and <code>WIRE_CUT_CRST</code> entries, which this daily cleanup leaves alone as an audit trail.</li>
              <li>Even those exempt entries don't last forever: <strong>every</strong> entry, exempt or not, is hard-deleted once it reaches <strong>6 months</strong> old.</li>
            </ul>
          </section>

          <section class="fi-section">
            <h5 class="fi-h5">Terms you'll see in the Activity Type column</h5>
            <div class="fi-glossary">
              <div class="fi-term">
                <dt>FUT_RTC / PST_RTC</dt>
                <dd>Clock drift &mdash; the device's own clock is running more than 5 minutes ahead of or behind the server's.</dd>
              </div>
              <div class="fi-term">
                <dt>MAIN_VTG</dt>
                <dd>Low main power &mdash; the vehicle's main power feeding the device has dropped below a safe level.</dd>
              </div>
              <div class="fi-term">
                <dt>INT_BAT_VTG</dt>
                <dd>Low backup battery &mdash; the device's own internal battery is running low.</dd>
              </div>
              <div class="fi-term">
                <dt>LAT_LONG</dt>
                <dd>Invalid GPS fix &mdash; the device reported (0, 0), meaning it hasn't locked onto GPS yet.</dd>
              </div>
              <div class="fi-term">
                <dt>SACK</dt>
                <dd>Panic re-acknowledgement &mdash; the device raised an emergency alert without a valid server address, so the server resends its own address.</dd>
              </div>
              <div class="fi-term">
                <dt>PrimaryIp / SecondaryIp</dt>
                <dd>Server address correction &mdash; the address the device is using doesn't match Device State Config, so the server pushes the correct one.</dd>
              </div>
              <div class="fi-term">
                <dt>VIN / UIN</dt>
                <dd>Identity correction &mdash; the chassis number or unique ID the device reports doesn't match the device record (set by Device VIN Config or an AIS-140 ticket), so the server pushes the correct value.</dd>
              </div>
              <div class="fi-term">
                <dt>WIRE_CUT_CRST</dt>
                <dd>Wire-cut reset &mdash; the device reports tampered/cut wiring; the server resets it, at most once every 5 days.</dd>
              </div>
              <div class="fi-term">
                <dt>DNF</dt>
                <dd>Device not found &mdash; the packet's IMEI/ID isn't in our records at all.</dd>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</div>
